import { CoinbaseWallet } from "@web3-react/coinbase-wallet";
import { initializeConnector, Web3ReactHooks } from "@web3-react/core";
import { GnosisSafe } from "@web3-react/gnosis-safe";
import { MetaMask } from "@web3-react/metamask";
import { Network } from "@web3-react/network";
import { Connector } from "@web3-react/types";
import { WalletConnect } from "@web3-react/walletconnect-v2";
import { RPC_PROVIDERS } from "./chain/providers";
import { memoize } from "lodash";
import {
  ChainIdLegacy,
  DEFAULT_CHAIN_ID,
  getEnvVarOrWarn,
  SUPPORTED_CHAINS,
} from "@silo/types";
import { getProxyRpcUrl } from "@silo/utils";

export enum ConnectionType {
  INJECTED = "INJECTED",
  COINBASE_WALLET = "COINBASE_WALLET",
  WALLET_CONNECT = "WALLET_CONNECT",
  NETWORK = "NETWORK",
  GNOSIS_SAFE = "GNOSIS_SAFE",
}

export interface Connection {
  connector: Connector;
  hooks: Web3ReactHooks;
  type: ConnectionType;
}

export type GetNetworkConnection = () => Connection;

function onError(error: Error) {
  console.debug(`web3-react error: ${error}`);
}

const getWeb3Network = () => {
  return initializeConnector<Network>(
    (actions) =>
      new Network({
        actions,
        urlMap: RPC_PROVIDERS(),
        defaultChainId: DEFAULT_CHAIN_ID,
      })
  );
};
export const getNetworkConnection = memoize((): Connection => {
  const [web3Network, web3NetworkHooks] = getWeb3Network();
  return {
    connector: web3Network,
    hooks: web3NetworkHooks,
    type: ConnectionType.NETWORK,
  };
});

const [web3Injected, web3InjectedHooks] = initializeConnector<MetaMask>(
  (actions) => new MetaMask({ actions, onError })
);
export const injectedConnection: Connection = {
  connector: web3Injected,
  hooks: web3InjectedHooks,
  type: ConnectionType.INJECTED,
};

const [web3GnosisSafe, web3GnosisSafeHooks] = initializeConnector<GnosisSafe>(
  (actions) => new GnosisSafe({ actions })
);
export const gnosisSafeConnection: Connection = {
  connector: web3GnosisSafe,
  hooks: web3GnosisSafeHooks,
  type: ConnectionType.GNOSIS_SAFE,
};

const walletConnectProjectId = getEnvVarOrWarn(
  () => process.env.NEXT_PUBLIC_WALLET_CONNECT_PROJECT_ID
);

const [web3WalletConnect, web3WalletConnectHooks] =
  initializeConnector<WalletConnect>(
    (actions) =>
      new WalletConnect({
        actions,
        options: {
          projectId: walletConnectProjectId,
          optionalChains: [...SUPPORTED_CHAINS],
          showQrModal: true,
        },
        onError,
      })
  );

export const walletConnectConnection: Connection = {
  connector: web3WalletConnect,
  hooks: web3WalletConnectHooks,
  type: ConnectionType.WALLET_CONNECT,
};

const [web3CoinbaseWallet, web3CoinbaseWalletHooks] =
  initializeConnector<CoinbaseWallet>(
    (actions) =>
      new CoinbaseWallet({
        actions,
        options: {
          url: getProxyRpcUrl(ChainIdLegacy.MAINNET),
          appName: "Silo Finance",
          reloadOnDisconnect: false,
        },
        onError,
      })
  );
export const coinbaseWalletConnection: Connection = {
  connector: web3CoinbaseWallet,
  hooks: web3CoinbaseWalletHooks,
  type: ConnectionType.COINBASE_WALLET,
};
